import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, CheckCircle, XCircle, Eye, ThumbsUp, ThumbsDown, MessageSquare, TrendingUp, TrendingDown, Shield } from 'lucide-react';
import { useBills } from '@/hooks/useBills';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface QualityIssue {
  type: 'low_confidence' | 'anomaly' | 'validation_error' | 'missing_data';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  field?: string;
  suggestion?: string;
}

export const QualityControlDashboard = () => {
  const { bills, isLoading } = useBills();
  const { toast } = useToast();
  const [selectedBill, setSelectedBill] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | 'needs_correction'>('approve');

  // Filter bills that need quality review
  const reviewBills = bills?.filter(bill => 
    bill.validation_status === 'review' || 
    bill.ai_confidence < 85 ||
    (bill.anomalies && Array.isArray(bill.anomalies) && bill.anomalies.length > 0)
  ) || [];

  const approvedBills = bills?.filter(bill => bill.validation_status === 'validated') || [];
  const rejectedBills = bills?.filter(bill => bill.validation_status === 'rejected') || [];

  // Calculate quality metrics
  const totalReviewed = approvedBills.length + rejectedBills.length;
  const approvalRate = totalReviewed > 0 ? Math.round((approvedBills.length / totalReviewed) * 100) : 0;
  const averageConfidence = bills && bills.length > 0 
    ? Math.round(bills.reduce((sum, bill) => sum + (bill.ai_confidence || 0), 0) / bills.length)
    : 0;

  const highConfidenceBills = bills?.filter(bill => (bill.ai_confidence || 0) >= 90).length || 0;
  const lowConfidenceBills = bills?.filter(bill => (bill.ai_confidence || 0) < 70).length || 0;

  // Analyze quality issues for a bill
  const analyzeQualityIssues = (bill: any): QualityIssue[] => {
    const issues: QualityIssue[] = [];

    // Low confidence check
    if ((bill.ai_confidence || 0) < 70) {
      issues.push({
        type: 'low_confidence',
        severity: (bill.ai_confidence || 0) < 50 ? 'critical' : 'high',
        description: `AI confidence is ${bill.ai_confidence || 0}% (below threshold)`,
        suggestion: 'Manual review recommended. Consider reprocessing with enhanced settings.'
      });
    }

    // Missing critical data
    if (!bill.amount || bill.amount <= 0) {
      issues.push({
        type: 'missing_data',
        severity: 'critical',
        description: 'Missing or invalid bill amount',
        field: 'amount',
        suggestion: 'Manually extract the total amount from the bill'
      });
    }

    if (!bill.bill_date) {
      issues.push({
        type: 'missing_data',
        severity: 'high',
        description: 'Missing bill date',
        field: 'bill_date',
        suggestion: 'Extract the bill date from the document'
      });
    }

    if (!bill.supplier_name) {
      issues.push({
        type: 'missing_data',
        severity: 'medium',
        description: 'Missing supplier/utility company name',
        field: 'supplier_name',
        suggestion: 'Identify the utility company from the bill header'
      });
    }

    // Anomaly detection
    if (bill.anomalies && Array.isArray(bill.anomalies) && bill.anomalies.length > 0) {
      bill.anomalies.forEach((anomaly: any) => {
        issues.push({
          type: 'anomaly',
          severity: anomaly.severity || 'medium',
          description: anomaly.description || 'Anomaly detected',
          field: anomaly.field,
          suggestion: anomaly.suggestion || 'Review the flagged data for accuracy'
        });
      });
    }

    // Validation errors
    if (bill.validation_status === 'error') {
      issues.push({
        type: 'validation_error',
        severity: 'high',
        description: 'Validation failed during processing',
        suggestion: 'Check processing logs for detailed error information'
      });
    }

    return issues;
  };

  const handleReviewAction = async (billId: string, action: 'approve' | 'reject' | 'needs_correction') => {
    try {
      const updateData: any = {
        validation_status: action === 'approve' ? 'validated' : action === 'reject' ? 'rejected' : 'review',
        reviewed_at: new Date().toISOString(),
      };

      if (reviewNotes.trim()) {
        updateData.notes = reviewNotes;
      }

      const { error } = await supabase
        .from('bills')
        .update(updateData)
        .eq('id', billId);

      if (error) throw error;

      toast({
        title: "Review completed",
        description: `Bill ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'marked for correction'}.`,
        variant: action === 'reject' ? 'destructive' : 'default',
      });

      setSelectedBill(null);
      setReviewNotes('');
      setReviewAction('approve');
    } catch (error) {
      console.error('Review action error:', error);
      toast({
        title: "Review failed",
        description: "An error occurred while saving the review.",
        variant: "destructive",
      });
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <Badge variant="destructive">Critical</Badge>;
      case 'high':
        return <Badge variant="destructive">High</Badge>;
      case 'medium':
        return <Badge variant="secondary">Medium</Badge>;
      case 'low':
        return <Badge variant="outline">Low</Badge>;
      default:
        return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'low_confidence':
        return <TrendingDown className="h-4 w-4 text-amber-500" />;
      case 'anomaly':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'validation_error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'missing_data':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Quality Control Dashboard</h2>
        <p className="text-muted-foreground">
          Review and validate AI-processed bills for accuracy and completeness
        </p>
      </div>

      {/* Quality Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Needs Review</CardTitle>
            <Eye className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{reviewBills.length}</div>
            <p className="text-xs text-muted-foreground">Pending review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approval Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{approvalRate}%</div>
            <p className="text-xs text-muted-foreground">
              {approvedBills.length}/{totalReviewed} approved
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Confidence</CardTitle>
            <Shield className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{averageConfidence}%</div>
            <p className="text-xs text-muted-foreground">AI accuracy</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Confidence</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{highConfidenceBills}</div>
            <p className="text-xs text-muted-foreground">≥90% confidence</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Low Confidence</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{lowConfidenceBills}</div>
            <p className="text-xs text-muted-foreground">&lt;70% confidence</p>
          </CardContent>
        </Card>
      </div>

      {/* Review Tabs */}
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending">Pending Review ({reviewBills.length})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({approvedBills.length})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({rejectedBills.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Bills Requiring Review</CardTitle>
              <CardDescription>
                Bills with low confidence, anomalies, or validation issues
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Issues</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        Loading review queue...
                      </TableCell>
                    </TableRow>
                  ) : reviewBills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No bills require review
                      </TableCell>
                    </TableRow>
                  ) : (
                    reviewBills.map((bill) => {
                      const issues = analyzeQualityIssues(bill);
                      const criticalIssues = issues.filter(i => i.severity === 'critical').length;
                      const highIssues = issues.filter(i => i.severity === 'high').length;

                      return (
                        <TableRow key={bill.id}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {bill.file_name || bill.bill_number || 'Unknown file'}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {bill.supplier_name || 'Unknown supplier'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Progress value={bill.ai_confidence || 0} className="w-16" />
                              <span className="text-sm font-medium">{bill.ai_confidence || 0}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {criticalIssues > 0 && (
                                <Badge variant="destructive" className="text-xs">
                                  {criticalIssues} Critical
                                </Badge>
                              )}
                              {highIssues > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  {highIssues} High
                                </Badge>
                              )}
                              {issues.length === 0 && (
                                <Badge variant="outline" className="text-xs">No issues</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono">
                            {bill.amount ? `$${bill.amount}` : '-'}
                          </TableCell>
                          <TableCell>
                            {bill.bill_date ? format(new Date(bill.bill_date), 'MMM dd, yyyy') : '-'}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedBill(bill)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approved">
          <Card>
            <CardHeader>
              <CardTitle>Approved Bills</CardTitle>
              <CardDescription>Bills that have passed quality review</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Reviewed</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvedBills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No approved bills yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    approvedBills.slice(0, 20).map((bill) => (
                      <TableRow key={bill.id}>
                        <TableCell className="font-medium">
                          {bill.file_name || bill.bill_number || 'Unknown file'}
                        </TableCell>
                        <TableCell className="font-mono">${bill.amount}</TableCell>
                        <TableCell>
                          <Badge variant="default">{bill.ai_confidence || 0}%</Badge>
                        </TableCell>
                        <TableCell>
                          {bill.reviewed_at ? format(new Date(bill.reviewed_at), 'MMM dd, HH:mm') : '-'}
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rejected">
          <Card>
            <CardHeader>
              <CardTitle>Rejected Bills</CardTitle>
              <CardDescription>Bills that failed quality review</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Reviewed</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rejectedBills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No rejected bills
                      </TableCell>
                    </TableRow>
                  ) : (
                    rejectedBills.slice(0, 20).map((bill) => (
                      <TableRow key={bill.id}>
                        <TableCell className="font-medium">
                          {bill.file_name || bill.bill_number || 'Unknown file'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="destructive">{bill.ai_confidence || 0}%</Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {bill.notes || 'Quality issues detected'}
                        </TableCell>
                        <TableCell>
                          {bill.reviewed_at ? format(new Date(bill.reviewed_at), 'MMM dd, HH:mm') : '-'}
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Modal */}
      {selectedBill && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-auto">
            <CardHeader>
              <CardTitle>Quality Review</CardTitle>
              <CardDescription>
                Review bill: {selectedBill.file_name || selectedBill.bill_number}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Bill Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Extracted Data</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Amount:</span>
                      <span className="font-mono">${selectedBill.amount || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Date:</span>
                      <span>{selectedBill.bill_date ? format(new Date(selectedBill.bill_date), 'MMM dd, yyyy') : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Supplier:</span>
                      <span>{selectedBill.supplier_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Confidence:</span>
                      <Badge variant={selectedBill.ai_confidence >= 85 ? 'default' : 'secondary'}>
                        {selectedBill.ai_confidence || 0}%
                      </Badge>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Quality Issues</h3>
                  <div className="space-y-2">
                    {analyzeQualityIssues(selectedBill).map((issue, index) => (
                      <div key={index} className="flex items-start space-x-2 p-2 rounded-lg bg-muted">
                        {getIssueIcon(issue.type)}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            {getSeverityBadge(issue.severity)}
                            <span className="text-sm font-medium">{issue.description}</span>
                          </div>
                          {issue.suggestion && (
                            <p className="text-xs text-muted-foreground mt-1">{issue.suggestion}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Review Actions */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Review Action</label>
                  <Select value={reviewAction} onValueChange={(value: any) => setReviewAction(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approve">
                        <div className="flex items-center space-x-2">
                          <ThumbsUp className="h-4 w-4 text-green-500" />
                          <span>Approve</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="needs_correction">
                        <div className="flex items-center space-x-2">
                          <MessageSquare className="h-4 w-4 text-amber-500" />
                          <span>Needs Correction</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="reject">
                        <div className="flex items-center space-x-2">
                          <ThumbsDown className="h-4 w-4 text-red-500" />
                          <span>Reject</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium">Review Notes</label>
                  <Textarea
                    placeholder="Add any notes about this review..."
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setSelectedBill(null)}>
                    Cancel
                  </Button>
                  <Button onClick={() => handleReviewAction(selectedBill.id, reviewAction)}>
                    Submit Review
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};